import {
  FunctionComponent,
  ReactNode,
  useRef,
  useState,
  useEffect,
} from "react";
import TextFieldAtom from "../../../atoms/textfield";
import { LabelPairedChevronDownSmBoldIcon } from "@deriv/quill-icons";
import { useClickOutside } from "../../../hooks/useClickOutside";
import { usePortalClick } from "../../../hooks/usePortalClick";
import DropdownContainerAtom, {
  DropdownDataType,
  DropdownItemType,
} from "../../../atoms/dropdown/container";

export type DropdownProps = {
  className?: string;
  label?: string;
  placeholder?: string;
  message?: string;

  /** Variant props */
  indicator?: "neutral" | "fail" | "success";
  state?: "default" | "disabled";
  type?: "outline" | "fill";
  size?: "lg" | "md" | "sm";
  textAlignment?: "left" | "center";
  variant?: "labelled" | "labelless";

  /** Custom input props */
  itemSize?: "md" | "sm";
  iconLeft?: ReactNode;
  labelLeft?: string;
  isRequired?: boolean;
  optionalText?: string;
  containerRef?: React.RefObject<HTMLDivElement>;
  customContent?: ReactNode;
  customValue?: string;
  onChange?: (item: DropdownItemType | DropdownItemType[]) => void;
  onClick?: () => void;

  /**Custom Dropdown props */
  data?: DropdownDataType[];
  selected?: DropdownItemType;

  /** Autocomplete props */
  autocomplete?: boolean;
};

export const Dropdown: FunctionComponent<DropdownProps> = ({
  className,
  label = "label",
  placeholder = "Select an option",
  message,
  indicator,
  state,
  type,
  size = "md",
  itemSize = "sm",
  textAlignment = "left",
  iconLeft,
  labelLeft,
  isRequired,
  optionalText,
  containerRef,
  selected: internalSelected,
  onChange,
  onClick,
  data = [],
  customValue,
  variant = "labelled",
  autocomplete = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState<DropdownItemType | undefined>(
    internalSelected
  );
  const [inputValue, setInputValue] = useState<string>(
    internalSelected?.label || ""
  );

  const defaultRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = containerRef || defaultRef;

  // Update internal state when props change
  useEffect(() => {
    if (internalSelected) {
      setSelected(internalSelected);
      setInputValue(internalSelected.label);
    }
  }, [internalSelected]);

  // Filter data based on input value
  const getFilteredData = () => {
    if (!autocomplete || !inputValue) {
      return data;
    }

    const searchTerm = inputValue.toLowerCase().trim();

    const results = data
      .map((group) => ({
        ...group,
        items: group.items.filter((item) =>
          item.label.toLowerCase().includes(searchTerm)
        ),
      }))
      .filter((group) => group.items.length > 0);

    if (results.length === 0) {
      return [
        {
          title: "No results found",
          items: [],
        },
      ];
    }

    return results;
  };

  const handleClick = (e: React.MouseEvent) => {
    const { isPortalClick } = usePortalClick(
      dropdownRef as React.RefObject<HTMLElement>
    );

    if (isPortalClick(e) || state === "disabled") {
      return;
    }

    setIsOpen(!isOpen);
    onClick?.();
  };

  const handleInputChange = (value: string | number) => {
    const newValue = value.toString();
    setInputValue(newValue);

    if (autocomplete && !isOpen) {
      setIsOpen(true);
    }
  };

  const handleItemSelect = (item: DropdownItemType) => {
    setSelected(item);
    setInputValue(item.label);
    setIsOpen(false);
    inputRef.current?.focus();
    onChange?.(item);
  };

  useClickOutside(
    dropdownRef,
    () => {
      setIsOpen(false);
    },
    ["quill-ui-next-dropdown-container"]
  );

  return (
    <TextFieldAtom
      ref={inputRef}
      containerRef={dropdownRef as React.RefObject<HTMLDivElement>}
      label={label}
      placeholder={placeholder}
      message={message}
      className={`quill-ui-next-pointer ${className}`}
      allowTyping={autocomplete}
      onClick={(e) => handleClick(e)}
      onChange={handleInputChange}
      iconRight={
        <LabelPairedChevronDownSmBoldIcon
          className={`quill-ui-next-rotatable ${
            isOpen && "quill-ui-next-rotate"
          }`}
        />
      }
      indicator={indicator}
      state={state}
      type={type}
      size={size}
      textAlignment={textAlignment}
      iconLeft={selected?.iconLeft || iconLeft}
      labelLeft={labelLeft}
      isRequired={isRequired}
      optionalText={optionalText}
      variant={variant}
      value={autocomplete ? inputValue : customValue || selected?.label}
      customContent={
        <DropdownContainerAtom
          width="100%"
          textAlignment={textAlignment}
          parent={dropdownRef}
          isOpen={isOpen}
          itemSize={itemSize}
          data={getFilteredData()}
          containerSize="md"
          selected={selected}
          onChange={(e: DropdownItemType | DropdownItemType[]) => {
            handleItemSelect(e as DropdownItemType);
          }}
        />
      }
    />
  );
};

export default Dropdown;
